<template>
  <div :id="id" ref="carouselEl" class="carousel slide image-carousel">
    <div class="carousel-inner">
      <div
        v-for="(img, i) in images"
        :key="i"
        class="carousel-item"
        :class="{ active: i === 0 }"
      >
        <img
          :src="img.src"
          class="carousel-img"
          :alt="img.alt || img.caption"
        />
        <div v-if="img.caption" class="image-carousel-caption text-center">
          {{ img.caption }}
        </div>
      </div>
    </div>

    <button
      class="carousel-control-prev"
      type="button"
      :data-bs-target="'#' + id"
      data-bs-slide="prev"
    >
      <span class="carousel-control-prev-icon" aria-hidden="true"></span>
      <span class="visually-hidden">Previous</span>
    </button>
    <button
      class="carousel-control-next"
      type="button"
      :data-bs-target="'#' + id"
      data-bs-slide="next"
    >
      <span class="carousel-control-next-icon" aria-hidden="true"></span>
      <span class="visually-hidden">Next</span>
    </button>

    <button
      type="button"
      class="carousel-playpause"
      :aria-label="playing ? 'Pause slideshow' : 'Play slideshow'"
      @click="toggle"
    >
      <font-awesome-icon
        :icon="playing ? 'fa-solid fa-pause' : 'fa-solid fa-play'"
      />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { Carousel } from "bootstrap";

interface CarouselImage {
  src: string;
  caption?: string;
  alt?: string;
}
interface CarouselHandle {
  cycle: () => void;
  pause: () => void;
  dispose: () => void;
}

export default defineComponent({
  name: "ImageCarouselComponent",
  props: {
    id: { type: String, required: true },
    images: { type: Array as PropType<CarouselImage[]>, required: true },
    interval: { type: Number, default: 4500 },
  },
  data() {
    return {
      playing: true,
      carousel: null as CarouselHandle | null,
    };
  },
  mounted() {
    this.carousel = Carousel.getOrCreateInstance(
      this.$refs.carouselEl as Element,
      {
        interval: this.interval,
        pause: false,
        wrap: true,
        ride: "carousel",
      },
    );
    this.carousel?.cycle();
    this.playing = true;
  },
  beforeUnmount() {
    this.carousel?.dispose();
  },
  methods: {
    toggle() {
      if (!this.carousel) return;
      if (this.playing) {
        this.carousel.pause();
      } else {
        this.carousel.cycle();
      }
      this.playing = !this.playing;
    },
  },
});
</script>

<style scoped>
.carousel-img {
  display: block;
  width: 100%;
  height: 21rem;
  object-fit: contain;
  background: #fff;
  border-radius: 4px;
}
@media (max-width: 767.98px) {
  .carousel-img {
    height: 16rem;
  }
}
.image-carousel-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.45rem 1rem;
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 0.9rem;
  border-radius: 0 0 4px 4px;
}
/* controls need a backing so the white chevrons show on the white images */
.carousel-control-prev-icon,
.carousel-control-next-icon {
  background-color: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  padding: 1.1rem;
  background-size: 42% 42%;
}
.carousel-playpause {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
}
</style>
